import {
  ArrayFormField,
  FileFormField,
  QuestionWithDetailsFormField,
  SingleFormField,
  StagedFieldsGroupFormField,
  ConditionalFieldsGroupFormField,
  FormConditionalValidation,
  FormValidation,
  FormValidationBasic,
  FormValidationIsTrue,
  FormValidationLength,
  FormValidationText,
  FormValidationValue,
  FormValidationBooleanSchema,
  FormValidationNumberSchema,
  FormValidationSchema,
  FormValidationStringSchema,
  FormConditionalValidationMatch,
  FormValidationMatch,
  FormFieldType,
  IFormCheckbox,
  IFormSelect,
} from '../typings';
import { config } from '../config';
import {
  asyncValidationsMapper,
  generateConditionalValidation,
  generateConditionalValidationFieldIds,
  syncValidationsMapper,
} from '.';
import { FormValidationSchemaBuilder } from '../builders';
import { GONE_SLOT_MESSAGE, goneSlotValues, selectValues } from './practiceSlots';
import { isPoBox, PO_BOX_NOT_ACCEPTED_MESSAGE } from './poBox';

/** True for a plain saved street that is a PO box; a picked suggestion (`[*]…`) never is. */
const isSavedPoBox = (value: unknown): boolean =>
  typeof value === 'string' &&
  !value.startsWith(config.externalLookupPrefix) &&
  isPoBox(value);

/** Select whose options use InternalLookup encoding (`[#]…[#]…` in option.value). */
const isSelectWithInternalLookupOptions = (
  field: SingleFormField,
): field is IFormSelect =>
  field.type === FormFieldType.Select &&
  Boolean(
    field.options?.some(option =>
      String(option.value).includes(config.internalLookupPrefix),
    ),
  );

/**
 * MaxLength for InternalLookup Select: encoded values are longer than the display label.
 * Skip length check for internal tokens; still enforce for custom (`[^]…`) and plain text.
 */
const maxLengthSelectInternalLookupTest = (
  maxLen: number,
): ((value: unknown) => boolean) => {
  return (value: unknown) => {
    if (value === undefined || value === null || value === '') return true;

    const normalized = Array.isArray(value)
      ? value.map(String).join(config.multiValueDivider)
      : String(value);

    const parts = normalized.split(config.multiValueDivider);
    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) continue;

      if (trimmed.startsWith(config.internalLookupPrefix)) continue;

      const text = trimmed.startsWith(config.customValuePrefix)
        ? trimmed.slice(config.customValuePrefix.length)
        : trimmed;

      if (text.length > maxLen) return false;
    }
    return true;
  };
};

const getSchemaFromValidations = (
  schema: FormValidationSchema,
  field:
    | SingleFormField
    | QuestionWithDetailsFormField
    | StagedFieldsGroupFormField
    | ArrayFormField
    | FileFormField
    | ConditionalFieldsGroupFormField,
  rule:
    | FormValidationBasic
    | FormValidationLength
    | FormValidationText
    | FormValidationValue
    | FormValidationIsTrue,
) => {
  // Always applied to the group's condition field, whose value is a plain string.
  if (field.type === FormFieldType.ConditionalFieldsGroup) {
    return (schema as FormValidationStringSchema).required('Required');
  }

  // An empty staged group shows only its Add button, so the message names what to add.
  if (field.type === FormFieldType.StagedFieldsGroup) {
    return (schema as FormValidationBooleanSchema).isTrue(
      `Add at least one ${field.title}.`,
    );
  }

  if (
    field.type === FormFieldType.QuestionWithDetails ||
    field.type === FormFieldType.Array ||
    field.type === FormFieldType.File
  ) {
    switch (rule.type) {
      case FormValidation.IsTrue:
        return (schema as FormValidationBooleanSchema).isTrue('Required');

      case FormValidation.MinValue:
        return (schema as FormValidationNumberSchema)
          .integer()
          .positive()
          .min(
            rule.value as number,
            `Required at least ${rule.value} file${
              rule.value === 1 ? '' : 's'
            }.`,
          );

      default:
        return (schema as FormValidationBooleanSchema).isTrue('Required');
    }
  }

  if (field.type === FormFieldType.Date) {
    switch (rule.type) {
      case FormValidation.MinValue:
      case FormValidation.MaxValue:
      default:
        break;
    }
  }

  switch (rule.type) {
    case FormValidation.Required:
      return schema.required('Required');

    case FormValidation.MinLength:
      return (schema as FormValidationStringSchema).min(
        rule.value,
        `${field.label} must be at least ${rule.value} characters`,
      );

    case FormValidation.MaxLength: {
      const maxLen = rule.value;
      // States what happens rather than restating the rule: the counter above the
      // message already shows the limit, and the discard is the part providers miss.
      const message = `Over the ${maxLen} character limit — this won't be saved until it's shorter.`;

      if (isSelectWithInternalLookupOptions(field)) {
        return (schema as FormValidationStringSchema).test(
          'maxLengthSelectInternalLookup',
          message,
          maxLengthSelectInternalLookupTest(maxLen),
        );
      }

      return (schema as FormValidationStringSchema).max(maxLen, message);
    }

    case FormValidation.Email:
      return (schema as FormValidationStringSchema).email(
        'Invalid email address',
      );

    case FormValidation.Url:
      return (schema as FormValidationStringSchema).url('Invalid URL');

    case FormValidation.Regex:
      return (schema as FormValidationStringSchema).matches(
        new RegExp(rule.value),
        'Invalid format',
      );

    case FormValidation.IsTrue:
      return (schema as FormValidationBooleanSchema).isTrue('Must be true');

    case FormValidation.Sync:
      return (schema as FormValidationStringSchema).test(
        syncValidationsMapper[rule.value],
      );

    case FormValidation.Async:
      return (schema as FormValidationStringSchema).test(
        asyncValidationsMapper[rule.value],
      );

    default:
      break;
  }
};

export const generateValidations = (
  field:
    | SingleFormField
    | QuestionWithDetailsFormField
    | StagedFieldsGroupFormField
    | ArrayFormField
    | FileFormField
    | ConditionalFieldsGroupFormField,
  conditions?: FormConditionalValidation[],
  validateMinFiles?: boolean,
) => {
  let schema: FormValidationSchema;

  switch (field.type) {
    case FormFieldType.File: {
      // File fields validate only the derived `${fieldId}_minFilesCount` value,
      // which stores the current uploaded file count.
      if (!validateMinFiles) return null;

      if (field.minFileCount <= 0) return null;

      const baseSchema = FormValidationSchemaBuilder.number();
      const rule: FormValidationValue = {
        type: FormValidation.MinValue,
        value: field.minFileCount,
      };

      let validatedSchema: FormValidationSchema = baseSchema;

      if (conditions && conditions.length > 0) {
        validatedSchema = baseSchema.when(
          generateConditionalValidationFieldIds(conditions),
          (values, sc) => {
            if (generateConditionalValidation(conditions, values)) {
              return getSchemaFromValidations(sc, field, rule) ?? sc;
            }
            return sc;
          },
        );
      } else {
        const newSchema = getSchemaFromValidations(baseSchema, field, rule);
        if (newSchema) validatedSchema = newSchema;
      }

      // Not Applicable bypass: if NA is checked, do not apply min-file validation.
      if (field.notApplicableField?.fieldId) {
        validatedSchema = baseSchema.when(field.notApplicableField.fieldId, {
          is: true,
          then: () => baseSchema,
          otherwise: () => validatedSchema,
        });
      }

      return validatedSchema;
    }

    // A required conditional fields group is enforced through its condition
    // field's own value — the only part of the group the provider can answer
    // directly. Its `${fieldId}_required` key has no runtime writer, so an
    // `isTrue` rule there would never clear.
    case FormFieldType.ConditionalFieldsGroup: {
      if (!field.isRequired) return null;

      schema = FormValidationSchemaBuilder.string();

      const rule = { type: FormValidation.Required } as FormValidationBasic;

      if (conditions) {
        schema = (schema as FormValidationStringSchema).when(
          generateConditionalValidationFieldIds(conditions),
          (values, sc) => {
            if (generateConditionalValidation(conditions, values)) {
              return getSchemaFromValidations(sc, field, rule) ?? sc;
            }
            return sc;
          },
        );
      } else {
        const newSchema = getSchemaFromValidations(schema, field, rule);

        if (newSchema) schema = newSchema;
      }

      break;
    }

    case FormFieldType.Array:
    case FormFieldType.StagedFieldsGroup:
    case FormFieldType.QuestionWithDetails: {
      schema =
        FormValidationSchemaBuilder.boolean();

      const rule =
        ({ type: FormValidation.IsTrue } as FormValidationIsTrue);

      if (conditions) {
        schema = (schema as FormValidationBooleanSchema).when(
          generateConditionalValidationFieldIds(conditions),
          (values, sc) => {
            if (generateConditionalValidation(conditions, values)) {
              return getSchemaFromValidations(sc, field, rule) ?? sc;
            } else {
              return sc;
            }
          },
        );
      } else {
        const newSchema = getSchemaFromValidations(schema, field, rule);

        if (newSchema) schema = newSchema;
      }

      break;
    }

    default: {
      schema =
        FormValidationSchemaBuilder[
          (field as IFormCheckbox).typeValue ?? 'string'
        ]();

      // A chosen practice location that is gone needs a new answer, whatever the field's own rules say.
      const goneValues =
        field.type === FormFieldType.Select
          ? goneSlotValues(field as IFormSelect)
          : new Set<string>();
      const withGoneSlotTest = (sc: FormValidationSchema) =>
        goneValues.size === 0
          ? sc
          : (sc as FormValidationStringSchema).test(
              'goneSlot',
              GONE_SLOT_MESSAGE,
              value => !selectValues(value).some(part => goneValues.has(part)),
            );

      // A PO box from prefill, older data or the API needs a street address. A locked
      // street is exempt, as on the server: the provider can't change it.
      const checksPoBox =
        field.type === FormFieldType.Select &&
        (field as IFormSelect).rejectsPoBox === true &&
        !(field as IFormSelect).readonly;
      const withPoBoxTest = (sc: FormValidationSchema) =>
        checksPoBox
          ? (sc as FormValidationStringSchema).test(
              'poBox',
              PO_BOX_NOT_ACCEPTED_MESSAGE,
              value => !isSavedPoBox(value),
            )
          : sc;

      const validations = field.validations ?? [];
      // Only a missing rule list skips the schema: an empty one still registers the field's plain schema.
      if (!field.validations && goneValues.size === 0 && !checksPoBox)
        return null;

      let combinedConditions = conditions ? [...conditions] : [];
      let filteredValidations = [...validations];

      const matchValidation = validations.find(
        v => v.type === FormValidation.Match,
      );

      if (matchValidation) {
        filteredValidations = filteredValidations.filter(
          v => v.type !== FormValidation.Match,
        );

        if (
          !filteredValidations.length &&
          goneValues.size === 0 &&
          !checksPoBox
        )
          return null;

        combinedConditions = [
          ...(conditions ?? []),
          {
            type: 'match',
            relatedFieldId: (matchValidation as FormValidationMatch).fieldId,
            value: (matchValidation as FormValidationMatch).value,
          } as FormConditionalValidationMatch,
        ];
      }

      if (combinedConditions) {
        schema = schema.when(
          generateConditionalValidationFieldIds(combinedConditions),
          (values, sc: FormValidationSchema) => {
            if (generateConditionalValidation(combinedConditions, values)) {
              let newSch = sc;
              for (const rule of filteredValidations) {
                const newSchema = getSchemaFromValidations(newSch, field, rule);

                if (newSchema) newSch = newSchema;
              }
              return withPoBoxTest(withGoneSlotTest(newSch));
            } else {
              return sc;
            }
          },
        );
      } else {
        for (const rule of validations) {
          const newSchema = getSchemaFromValidations(schema, field, rule);

          if (newSchema) schema = newSchema;
        }
        schema = withPoBoxTest(withGoneSlotTest(schema));
      }

      break;
    }
  }

  return schema;
};
